import { useRef, useState, type CSSProperties, type JSX, type ReactNode, type RefObject } from 'react';

import { DisabledReason } from './DisabledReason';
import {
  assertConfirmPolicy,
  dangerColorToken,
  dangerSoftToken,
  type DangerLevel,
  type DangerPolicy,
} from './danger';

/**
 * 确认内容（后果 + 确认短语 + 两个按钮）。
 *
 * 抽出来的唯一理由：**按钮确认气泡**（`useConfirm`）与**受控确认浮层**（`ConfirmDialog`）
 * 必须长得一模一样、说得一模一样。让两条路径各自渲染一遍"后果文案 + 危险等级配色"，
 * 迟早会出现"从按钮上弹出来的那个少了一句后果"—— 而那正是这类组件最不能出的错。
 *
 * 因此这个组件不带 `open`：它的存在就等于"打开着"。于是已输入的确认短语天然随卸载清空，
 * 不需要"关闭时清空"这类额外的 effect（少一条能被忘记执行的清理路径）。
 */

export interface ConfirmSurfaceProps {
  danger: DangerLevel;
  /** 后果。完整句子，写清"会发生什么 + 能不能撤销" */
  consequence: string;
  confirmLabel: string;
  confirmPhrase?: string | undefined;
  cancelLabel?: string | undefined;
  detail?: ReactNode;
  /**
   * 「取消」按钮的 ref。
   * 由宿主接管初始焦点：规则是**无例外**地落在取消上（见 danger.ts）。
   * 不传也行（组件内部自备一个），但宿主通常需要它来指定初始焦点。
   */
  cancelRef?: RefObject<HTMLButtonElement | null> | undefined;
  /**
   * 后果那段的 id。
   *
   * 两处引用它：面板（`aria-describedby`）与**取消按钮**。
   * 只挂在面板上是不够的 —— 初始焦点落在取消按钮上，读屏念的是那个按钮，
   * 而飞行员最需要先听到的正是"会发生什么"。
   */
  consequenceId?: string | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmSurface(props: ConfirmSurfaceProps): JSX.Element {
  const { danger, consequence, confirmLabel, detail, onCancel, onConfirm, cancelRef } = props;
  // 策略在渲染期强制：一个"忘了写后果"的确认框看起来完全正常，直到有人按下去
  const policy = assertConfirmPolicy({
    danger,
    consequence,
    confirmLabel,
    confirmPhrase: props.confirmPhrase,
  });
  const consequenceId = props.consequenceId;

  const cancelLabel = props.cancelLabel ?? '取消';
  const confirmPhrase = props.confirmPhrase ?? '';
  const [typed, setTyped] = useState('');
  const fallbackCancelRef = useRef<HTMLButtonElement | null>(null);
  const resolvedCancelRef = cancelRef ?? fallbackCancelRef;

  const phraseSatisfied = !policy.requireConfirmPhrase || typed.trim() === confirmPhrase.trim();
  const blockedReason = phraseSatisfied ? undefined : `请先逐字输入「${confirmPhrase}」`;

  return (
    <div data-hgc-confirm-surface="" style={SURFACE}>
      <p
        id={consequenceId}
        data-hgc-confirm-consequence=""
        data-danger={policy.consequenceTone}
        style={consequenceStyle(policy.consequenceTone)}
      >
        {consequence}
      </p>

      {detail !== undefined ? <div data-hgc-confirm-detail="">{detail}</div> : null}

      {policy.requireConfirmPhrase ? (
        <label style={PHRASE_LABEL}>
          <span style={PHRASE_HINT}>{`请逐字输入「${confirmPhrase}」以确认`}</span>
          <input
            type="text"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            aria-label="确认短语"
            autoComplete="off"
            spellCheck={false}
            data-hgc-confirm-phrase=""
            style={PHRASE_INPUT}
          />
        </label>
      ) : null}

      <div style={FOOTER}>
        <button
          ref={resolvedCancelRef}
          type="button"
          onClick={onCancel}
          aria-describedby={consequenceId}
          data-hgc-confirm-cancel=""
          data-emphasis="safe"
          style={{ ...BUTTON_BASE, ...SAFE_BUTTON }}
        >
          {cancelLabel}
        </button>
        <DisabledReason reason={blockedReason}>
          <button
            type="button"
            onClick={onConfirm}
            data-hgc-confirm-accept=""
            data-danger={danger}
            data-emphasis={policy.confirmEmphasis}
            style={{ ...BUTTON_BASE, ...confirmButtonStyle(policy) }}
          >
            {confirmLabel}
          </button>
        </DisabledReason>
      </div>
    </div>
  );
}

/** 外层只是纵向排布：气泡与居中弹窗共用同一份排版 */
const SURFACE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const BUTTON_BASE: CSSProperties = {
  padding: '0.375rem 0.875rem',
  borderRadius: 'var(--radius-control, 0.5rem)',
  fontSize: '0.8125rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

/** 安全出口：主色实心。见 ConfirmDialog 文件头部取舍 3 */
const SAFE_BUTTON: CSSProperties = {
  background: 'var(--accent)',
  color: 'var(--accent-fg)',
  border: '1px solid var(--accent)',
};

function confirmButtonStyle(policy: DangerPolicy): CSSProperties {
  const tone = dangerColorToken(policy.consequenceTone);
  switch (policy.confirmEmphasis) {
    case 'secondary':
      return { background: 'transparent', color: 'var(--fg)', border: '1px solid var(--line-strong)' };
    case 'outlined-warning':
    case 'outlined-critical':
      // 描边而非实心：见 ConfirmDialog 文件头部取舍 2
      return { background: 'transparent', color: tone, border: `1px solid ${tone}` };
  }
}

function consequenceStyle(tone: DangerLevel): CSSProperties {
  return {
    margin: 0,
    padding: '0.5rem 0.75rem',
    background: dangerSoftToken(tone),
    color: 'var(--fg)',
    // 左侧粗描边比整块底色更能把"这是后果"标出来，同时不牺牲文字对比度
    borderLeft: `3px solid ${dangerColorToken(tone)}`,
    borderRadius: 'var(--radius-control, 0.5rem)',
    fontSize: '0.8125rem',
    lineHeight: 1.5,
  };
}

const PHRASE_LABEL: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
};

const PHRASE_HINT: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--fg-muted)',
};

const PHRASE_INPUT: CSSProperties = {
  padding: '0.375rem 0.5rem',
  borderRadius: 'var(--radius-control, 0.5rem)',
  border: '1px solid var(--line-strong)',
  background: 'var(--surface-2)',
  color: 'var(--fg)',
  fontFamily: 'inherit',
  fontSize: '0.8125rem',
};

const FOOTER: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '0.5rem',
};
